import { NavLink } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, GraduationCap, ClipboardCheck, Coins, Menu, Users, Navigation,
  Wallet, QrCode, ShoppingBag, Megaphone, Briefcase, ListChecks, CalendarDays, FileText,
} from "lucide-react";

/**
 * Native-style mobile bottom tab bar. Shows 4 role-adapted primary destinations
 * + a "More" button that opens the full navigation drawer. Hidden on desktop (lg+).
 */
const PRIMARY_BY_ROLE = {
  super_admin: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/students", key: "students", icon: GraduationCap },
    { to: "/reports", key: "reports", icon: FileText },
    { to: "/hr/employees", key: "employees", icon: Briefcase },
  ],
  school_admin: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/students", key: "students", icon: GraduationCap },
    { to: "/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/fees", key: "fees", icon: Coins },
  ],
  academic: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/students", key: "students", icon: GraduationCap },
    { to: "/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/grades-entry", key: "gradesEntry", icon: ListChecks },
  ],
  teacher: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/quizzes", key: "quizzes", icon: ClipboardCheck },
    { to: "/grades-entry", key: "gradesEntry", icon: ListChecks },
  ],
  parent: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/fees", key: "fees", icon: Coins },
    { to: "/canteen/wallet", key: "canteen_wallet", icon: Wallet },
  ],
  student: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/timetable", key: "timetable", icon: CalendarDays },
    { to: "/quizzes", key: "quizzes", icon: ClipboardCheck },
    { to: "/canteen/my-wallet", key: "canteen_my_wallet", icon: QrCode },
  ],
  driver: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/my-route", key: "my_route", icon: Navigation },
    { to: "/announcements", key: "announcements", icon: Megaphone },
  ],
  finance: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/fees", key: "fees", icon: Coins },
    { to: "/canteen", key: "canteen_dashboard", icon: ShoppingBag },
    { to: "/reports", key: "reports", icon: FileText },
  ],
  hr: [
    { to: "/", key: "overview", icon: LayoutDashboard, end: true },
    { to: "/hr/employees", key: "employees", icon: Briefcase },
    { to: "/parents", key: "parents", icon: Users },
    { to: "/hr", key: "hr_dashboard", icon: LayoutDashboard },
  ],
};

export default function MobileBottomNav({ onOpenMenu }) {
  const { t } = useI18n();
  const { user } = useAuth();
  if (!user) return null;
  const items = PRIMARY_BY_ROLE[user.role] || PRIMARY_BY_ROLE.school_admin;

  const linkCls = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 min-tap-target py-1 rounded-xl transition-all duration-200 active:scale-95 ${
      isActive ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <nav
      className="lg:hidden fixed bottom-0 start-0 w-full z-50 glass-panel border-t pb-safe"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around h-16 px-1">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} className={linkCls} data-testid={`bottomnav-${it.key}`}>
            {({ isActive }) => (
              <>
                <span className={`grid place-items-center rounded-full px-3 py-1 transition-colors ${isActive ? "bg-primary/10" : ""}`}>
                  <it.icon className="w-5 h-5" />
                </span>
                <span className="text-[10px] font-medium leading-none truncate max-w-[64px]">{t(it.key)}</span>
              </>
            )}
          </NavLink>
        ))}
        <button
          onClick={onOpenMenu}
          className="flex flex-col items-center justify-center gap-0.5 flex-1 min-tap-target py-1 rounded-xl text-muted-foreground active:scale-95 transition-all duration-200"
          data-testid="bottomnav-more"
        >
          <span className="grid place-items-center px-3 py-1"><Menu className="w-5 h-5" /></span>
          <span className="text-[10px] font-medium leading-none">{t("more") || "المزيد"}</span>
        </button>
      </div>
    </nav>
  );
}
